import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Mail, 
  Calendar, 
  Target, 
  TrendingUp, 
  ArrowRight,
  Sparkles,
  Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const pipelineStages = [
    { key: 'discovered', label: 'Discovered', color: 'bg-gray-100 text-gray-700' },
    { key: 'reviewed', label: 'Reviewed', color: 'bg-blue-100 text-blue-700' },
    { key: 'outreach_sent', label: 'Outreach Sent', color: 'bg-purple-100 text-purple-700' },
    { key: 'replied', label: 'Replied', color: 'bg-amber-100 text-amber-700' },
    { key: 'meeting_scheduled', label: 'Meeting Scheduled', color: 'bg-cyan-100 text-cyan-700' },
    { key: 'active_client', label: 'Active Client', color: 'bg-green-100 text-green-700' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of your lead generation pipeline</p>
        </div>
        <Button asChild className="bg-forest hover:bg-forest-dark text-white" data-testid="run-discovery-btn">
          <Link to="/campaigns">
            <Target className="mr-2 h-4 w-4" />
            Run Discovery
          </Link>
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-stone-100 hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Leads</CardTitle>
            <Users className="h-4 w-4 text-forest" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-heading font-bold">{stats?.total_leads || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">In your pipeline</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-stone-100 hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Emails Sent</CardTitle>
            <Mail className="h-4 w-4 text-wheat" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-heading font-bold">{stats?.emails?.sent || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">{stats?.emails?.drafts || 0} drafts pending</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-stone-100 hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Upcoming Meetings</CardTitle>
            <Calendar className="h-4 w-4 text-cyan-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-heading font-bold">{stats?.upcoming_meetings || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Scheduled</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-stone-100 hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Campaigns</CardTitle>
            <Target className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-heading font-bold">{stats?.active_campaigns || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Running weekly</p>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Overview */}
      <Card className="bg-white border-stone-100">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading font-semibold text-lg">Pipeline Overview</CardTitle>
          <Button variant="ghost" asChild className="text-forest hover:text-forest-dark" data-testid="view-pipeline-btn">
            <Link to="/pipeline">
              View Pipeline
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {pipelineStages.map((stage) => (
              <div key={stage.key} className="text-center">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${stage.color} mb-2`}>
                  <span className="font-heading font-bold text-lg">
                    {stats?.pipeline?.[stage.key] || 0}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{stage.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Leads & Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <Card className="bg-white border-stone-100">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading font-semibold text-lg">Recent Leads</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {stats?.recent_leads?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_leads.map((lead) => (
                  <Link 
                    key={lead.lead_id} 
                    to={`/leads/${lead.lead_id}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-sage transition-colors group"
                    data-testid={`recent-lead-${lead.lead_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate group-hover:text-forest transition-colors">
                        {lead.business_name}
                      </p>
                      <p className="text-sm text-muted-foreground">{lead.segment_name || 'Uncategorized'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {lead.claude_score && (
                        <span className={`
                          px-2 py-1 rounded-full text-xs font-medium
                          ${lead.claude_score >= 70 ? 'score-high' : lead.claude_score >= 40 ? 'score-medium' : 'score-low'}
                        `}>
                          {lead.claude_score}
                        </span>
                      )}
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-forest transition-colors" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">No leads yet</p>
                <Button asChild variant="link" className="text-forest mt-2">
                  <Link to="/campaigns">Run your first discovery</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-white border-stone-100">
          <CardHeader>
            <CardTitle className="font-heading font-semibold text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link 
              to="/campaigns"
              className="flex items-center gap-4 p-4 rounded-xl bg-sage hover:bg-forest hover:text-white transition-all group"
              data-testid="quick-action-discovery"
            >
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <Target className="h-5 w-5 text-forest group-hover:text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Create Campaign</p>
                <p className="text-sm opacity-70">Set up a new discovery sweep</p>
              </div>
              <ArrowRight className="h-5 w-5 opacity-50" />
            </Link>

            <Link 
              to="/segments"
              className="flex items-center gap-4 p-4 rounded-xl bg-sage hover:bg-forest hover:text-white transition-all group"
              data-testid="quick-action-segments"
            >
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <Users className="h-5 w-5 text-forest group-hover:text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Edit Segments</p>
                <p className="text-sm opacity-70">Customize segment profiles</p>
              </div>
              <ArrowRight className="h-5 w-5 opacity-50" />
            </Link>

            <Link 
              to="/emails"
              className="flex items-center gap-4 p-4 rounded-xl bg-sage hover:bg-forest hover:text-white transition-all group"
              data-testid="quick-action-emails"
            >
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <Sparkles className="h-5 w-5 text-forest group-hover:text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Review Drafts</p>
                <p className="text-sm opacity-70">AI-drafted emails waiting for approval</p>
              </div>
              <ArrowRight className="h-5 w-5 opacity-50" />
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
